import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import classNames from "classnames";
import { memo, ReactNode } from "react";
import * as styles from "./ask-redo-disclosure-button.module.css";

/**
 * Click-to-expand row used by the tool group header, each tool row,
 * and the reasoning panel. They share the same shape:
 *
 *   `<button aria-expanded><icon?/><label/><Chevron rotated={isOpen}/></button>`
 *
 * Owning the shape here keeps the three call sites from drifting on
 * hover background, focus ring, chevron rotation duration, and
 * `prefers-reduced-motion` handling.
 *
 * `children` is the label slot — supply any combination of an icon
 * + a `<Text>` element + extra metadata. The chevron is appended
 * automatically.
 */
export interface AskRedoDisclosureButtonProps {
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
  className?: string;
}

export const AskRedoDisclosureButton = memo(function AskRedoDisclosureButton({
  isOpen,
  onToggle,
  children,
  className,
}: AskRedoDisclosureButtonProps) {
  return (
    <button
      aria-expanded={isOpen}
      className={classNames(styles.button, className)}
      onClick={onToggle}
      type="button"
    >
      {children}
      <ChevronDownIcon
        aria-hidden="true"
        className={classNames(styles.chevron, isOpen && styles.chevronOpen)}
      />
    </button>
  );
});
